import React, { useState, useEffect } from 'react';
import { useOutletContext, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Panel, Chip, Btn, Dot, Toggle, Portrait, PageHead, I } from '../components/hud';

const API_BASE_URL = import.meta.env.VITE_API_URL || (import.meta.env.PROD ? '' : 'http://localhost:5000');

function Dashboard() {
  const { user } = useOutletContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const permissions = user?.permissions || {};

  // Discord state
  const [discordStatus, setDiscordStatus] = useState({
    linked: false,
    discordUsername: null,
    notificationPreferences: {
      eventReminders: true,
      feeReminders: true,
      corpAnnouncements: true
    }
  });
  const [discordLoading, setDiscordLoading] = useState(true);
  const [discordMessage, setDiscordMessage] = useState(null);

  // My Alts state
  const [myAlts, setMyAlts] = useState({ characters: [] });
  const [altsLoading, setAltsLoading] = useState(true);

  // Check for Discord callback
  useEffect(() => {
    if (searchParams.get('discord') === 'linked') {
      setDiscordMessage({ type: 'success', text: 'Discord account linked successfully!' });
      searchParams.delete('discord');
      setSearchParams(searchParams);
      fetchDiscordStatus();
    } else if (searchParams.get('error')?.startsWith('discord')) {
      setDiscordMessage({ type: 'error', text: 'Failed to link Discord account. Please try again.' });
      searchParams.delete('error');
      setSearchParams(searchParams);
    }
  }, [searchParams]);

  // Fetch Discord status
  const fetchDiscordStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/auth/discord/status`, {
        withCredentials: true
      });
      setDiscordStatus(response.data);
    } catch (error) {
      console.error('Error fetching Discord status:', error);
    } finally {
      setDiscordLoading(false);
    }
  };

  useEffect(() => {
    fetchDiscordStatus();
    fetchMyAlts();
  }, []);

  // Fetch My Alts
  const fetchMyAlts = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/members/my-alts`, {
        withCredentials: true
      });
      setMyAlts(response.data);
    } catch (error) {
      console.error('Error fetching alts:', error);
    } finally {
      setAltsLoading(false);
    }
  };

  const handleLinkDiscord = () => {
    window.location.href = `${API_BASE_URL}/auth/discord`;
  };

  const handleUnlinkDiscord = async () => {
    try {
      await axios.post(`${API_BASE_URL}/auth/discord/unlink`, {}, { withCredentials: true });
      setDiscordStatus({
        linked: false,
        discordUsername: null,
        notificationPreferences: discordStatus.notificationPreferences
      });
      setDiscordMessage({ type: 'success', text: 'Discord account unlinked.' });
    } catch (error) {
      console.error('Error unlinking Discord:', error);
      setDiscordMessage({ type: 'error', text: 'Failed to unlink Discord account.' });
    }
  };

  const handlePreferenceChange = async (key) => {
    const newPrefs = {
      ...discordStatus.notificationPreferences,
      [key]: !discordStatus.notificationPreferences[key]
    };
    try {
      await axios.put(`${API_BASE_URL}/auth/discord/preferences`, newPrefs, { withCredentials: true });
      setDiscordStatus((prev) => ({ ...prev, notificationPreferences: newPrefs }));
    } catch (error) {
      console.error('Error updating preferences:', error);
    }
  };

  const permissionItems = [
    { key: 'canViewMembers', label: 'View Members' },
    { key: 'canTrackMembers', label: 'Track Members' },
    { key: 'canViewWallet', label: 'View Wallet' },
    { key: 'canViewStructures', label: 'View Structures' },
    { key: 'canViewAssets', label: 'View Assets' },
    { key: 'canViewBlueprints', label: 'View Blueprints' },
    { key: 'canViewContracts', label: 'View Contracts' },
    { key: 'canViewIndustry', label: 'View Industry' },
    { key: 'canViewMining', label: 'View Mining' },
    { key: 'canViewKillmails', label: 'View Killmails' }
  ];
  const missingScopes = permissionItems.filter((p) => !permissions[p.key]).length;

  const rankChip = permissions.isCEO ? (
    <Chip tone="amber">CEO · CLEARANCE Ω</Chip>
  ) : permissions.isDirector ? (
    <Chip tone="magenta">DIRECTOR</Chip>
  ) : (
    <Chip tone="ghost">PILOT</Chip>
  );

  const prefRows = [
    ['eventReminders', 'Fleet & Event Reminders'],
    ['feeReminders', 'Fee & Tax Reminders'],
    ['corpAnnouncements', 'Corporation Broadcasts']
  ];

  const altCharacters =
    myAlts.characters?.length > 0
      ? myAlts.characters
      : user?.characterId
      ? [{ characterId: user.characterId, characterName: user.characterName, isMain: true, isCurrentUser: true }]
      : [];

  return (
    <>
      <PageHead crumb="OPS // OVERVIEW" title="Command Dashboard" />

      <div className="divlabel">Pillar I · Access &amp; Comms</div>
      <div className="grid-2">
        <Panel title="Access Matrix" sub="ESI · ROLES" action={rankChip}>
          <div className="access-grid">
            {permissionItems.map((item) => {
              const ok = !!permissions[item.key];
              return (
                <div key={item.key} className={`access-row ${ok ? 'granted' : 'denied'}`}>
                  <span>{item.label}</span>
                  <span className="stat">{ok ? '● GRANTED' : '○ DENIED'}</span>
                </div>
              );
            })}
          </div>
          {missingScopes > 0 && (
            <div
              style={{
                marginTop: 14,
                padding: '10px 12px',
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: 'var(--text-2)',
                background: 'rgba(255,181,71,0.06)',
                borderLeft: '2px solid var(--amber)'
              }}
            >
              {missingScopes} scope{missingScopes > 1 ? 's' : ''} unavailable for your roles · derived from ESI &amp;
              corporation roles.
            </div>
          )}
        </Panel>

        <Panel
          title="Comms Relay"
          sub="DISCORD"
          action={
            discordLoading ? (
              <Chip tone="ghost">SYNC…</Chip>
            ) : discordStatus.linked ? (
              <Chip tone="ok"><Dot />LINKED</Chip>
            ) : (
              <Chip tone="danger">OFFLINE</Chip>
            )
          }
        >
          {discordMessage && (
            <div
              style={{
                marginBottom: 12,
                padding: '10px 12px',
                fontFamily: 'var(--font-mono)',
                fontSize: 11,
                color: discordMessage.type === 'error' ? 'var(--danger)' : 'var(--ok)',
                background:
                  discordMessage.type === 'error' ? 'rgba(255,91,106,0.08)' : 'rgba(91,217,154,0.08)',
                borderLeft: `2px solid ${discordMessage.type === 'error' ? 'var(--danger)' : 'var(--ok)'}`
              }}
            >
              {discordMessage.text}
            </div>
          )}

          <div className="dscrd">
            {discordLoading ? (
              <div className="label">Establishing relay…</div>
            ) : discordStatus.linked ? (
              <>
                <div className="dscrd-status">
                  <Dot />
                  <span className="hdl">{discordStatus.discordUsername}</span>
                  <span style={{ marginLeft: 'auto', color: 'var(--text-3)' }}>relay · linked</span>
                </div>
                <div>
                  <div className="label" style={{ marginBottom: 4 }}>NOTIFICATION CHANNELS</div>
                  <div className="dscrd-toggles">
                    {prefRows.map(([k, l]) => (
                      <div key={k} className="toggle-row">
                        <span>{l}</span>
                        <Toggle
                          on={!!discordStatus.notificationPreferences?.[k]}
                          onChange={() => handlePreferenceChange(k)}
                        />
                      </div>
                    ))}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                  <Btn tone="danger" onClick={handleUnlinkDiscord}>Unlink</Btn>
                </div>
              </>
            ) : (
              <>
                <p
                  style={{
                    fontFamily: 'var(--font-mono)',
                    fontSize: 11,
                    color: 'var(--text-2)',
                    letterSpacing: '0.04em',
                    margin: '0 0 4px',
                    lineHeight: 1.6
                  }}
                >
                  Link your Discord account to receive event reminders, fee notices and corporation broadcasts.
                </p>
                <Btn tone="primary" onClick={handleLinkDiscord}>Link Discord Account</Btn>
              </>
            )}
          </div>
        </Panel>
      </div>

      <div className="divlabel">Pillar II · Pilots</div>
      <Panel
        title="Pilot Roster"
        sub={`${altCharacters.length} LINKED CHARACTER${altCharacters.length === 1 ? '' : 'S'}`}
      >
        {altsLoading ? (
          <div className="label">Resolving linked characters…</div>
        ) : (
          <div className="alts">
            {altCharacters.map((char) => (
              <div key={char.characterId} className={`alt ${char.isCurrentUser ? 'current' : ''}`}>
                <Portrait
                  name={char.characterName}
                  size={40}
                  src={`https://images.evetech.net/characters/${char.characterId}/portrait?size=64`}
                />
                <div style={{ minWidth: 0 }}>
                  <div className="nm">{char.characterName}</div>
                  <div className="id">CHR · {char.characterId}</div>
                </div>
                <div className="right">
                  {char.isMain && <Chip tone="amber">★ MAIN</Chip>}
                  {char.isCurrentUser && (
                    <span
                      style={{
                        fontFamily: 'var(--font-mono)',
                        fontSize: 9.5,
                        letterSpacing: '0.2em',
                        color: 'var(--accent)'
                      }}
                    >
                      ● ACTIVE
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </>
  );
}

export default Dashboard;
